import fs from 'node:fs'
import path from 'node:path'
import type { ResolvedBetterstartConfig } from '@cli-types/core-engine/config.js'
import type { InstalledPresetManifest } from '@cli-types/preset-engine/presets.js'
import { betterstartDir, removePath } from '@cli-utils/paths.js'
import { deleteSnapshot } from '@core-engine/snapshots/store/delete-snapshot.js'
import { hasTombstone } from '@core-engine/snapshots/store/has-tombstone.js'
import { loadManifest } from '@core-engine/snapshots/store/load-manifest.js'
import { removeSchemaOwner } from '@preset-engine/ownership/remove-schema-owner.js'
import { resolveProjectPaths } from '@next-adapter/config/paths/resolve-project-paths.js'

export function removePresetSchemas(
  cwd: string,
  config: ResolvedBetterstartConfig,
  manifest: InstalledPresetManifest
): string[] {
  const paths = resolveProjectPaths(config)
  const deletedPaths: string[] = []
  const manifestsBySchema = new Map<string, NonNullable<ReturnType<typeof loadManifest>>>()
  const missingManifests: string[] = []

  for (const schemaName of manifest.ownedSchemas) {
    const scopeManifest = loadManifest(cwd, schemaName)
    if (scopeManifest) {
      manifestsBySchema.set(schemaName, scopeManifest)
    } else {
      missingManifests.push(schemaName)
    }
  }

  if (missingManifests.length > 0) {
    throw new Error(
      `Cannot remove ${manifest.id}. Missing snapshot manifest${missingManifests.length === 1 ? '' : 's'} for preset-owned schema${missingManifests.length === 1 ? '' : 's'}: ${missingManifests.join(', ')}. Re-run \`pnpm betterstart init --force\` to restore snapshot state before removing the preset.`
    )
  }

  for (const schemaName of manifest.ownedSchemas) {
    const scopeManifest = manifestsBySchema.get(schemaName)
    if (scopeManifest) {
      for (const filePath of [
        ...scopeManifest.files.map((entry) => entry.path),
        ...scopeManifest.skipped
      ]) {
        if (removePath(cwd, filePath)) {
          deletedPaths.push(filePath)
        }
      }
      deleteSnapshot(cwd, schemaName)
    }

    const schemaPath = path.posix.join(paths.schemasDir, `${schemaName}.json`)
    const formsSchemaPath = path.posix.join(paths.schemasDir, 'forms', `${schemaName}.json`)
    if (removePath(cwd, schemaPath)) {
      deletedPaths.push(schemaPath)
    }
    if (removePath(cwd, formsSchemaPath)) {
      deletedPaths.push(formsSchemaPath)
    }
    if (hasTombstone(cwd, schemaName)) {
      fs.rmSync(betterstartDir(cwd, 'snapshots', '_removed', schemaName), {
        force: true
      })
    }
    removeSchemaOwner(cwd, schemaName)
  }

  return deletedPaths
}
